import {
  Box,
  Button,
  HStack,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useMemo, useState } from 'react'

import { Checkbox, Modal } from '@mangovoice/mango-ui'

import { useScheduleI18n } from '../../i18n'
import type {
  ScheduleEventCreate,
  ScheduleHoliday,
  ScheduleModalNotification,
} from '../../types'
import { dsText, fontSize, processScheduleEvents, tokenVar } from '../../utils'
import {
  createScheduleModalNotifier,
  scheduleModalBodyTextStyles,
} from '../shared'

export interface AddHolidaysModalProps {
  isOpen: boolean
  onClose: () => void
  scheduleTimeZone: string
  holidays: ScheduleHoliday[]
  isLoadingHolidays?: boolean
  holidaysError?: { message?: string } | null
  onAddHolidays: (holidays: ScheduleEventCreate[]) => void
  onOpenCustomOverride?: () => void
  onNotify?: (notification: ScheduleModalNotification) => void
}

/**
 * Format the next occurrence date for a holiday
 */
const formatNextOccurrence = (
  holiday: ScheduleHoliday,
  scheduleTimeZone: string,
  locale: string,
  unknownLabel: string,
  weekdayLabels: Record<number, string>,
): string => {
  try {
    // Create a minimal ScheduleEvent-like object to use processScheduleEvents
    const eventLike = {
      id: 0,
      schedule_id: 0,
      dtstart: holiday.dtstart,
      dtend: holiday.dtend,
      duration: holiday.duration,
      rrule: holiday.rrule,
      rdate: holiday.rdate,
      exdate: holiday.exdate,
      fbtype: 'BUSY' as const,
      switchboard_routing: holiday.switchboard_routing || ('HOLIDAY' as const),
      summary: holiday.summary,
      created_at: '',
      updated_at: '',
    }

    const helpers = processScheduleEvents([eventLike], scheduleTimeZone)
    if (helpers.length > 0) {
      const helper = helpers[0]
      const startZdt = helper.zonedDateTimeStart
      const weekdayLabel = weekdayLabels[startZdt.dayOfWeek] || ''
      const utcDate = new Date(
        Date.UTC(startZdt.year, startZdt.month - 1, startZdt.day, 12, 0, 0, 0),
      )
      const dateLabel = new Intl.DateTimeFormat(locale, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(utcDate)
      return weekdayLabel ? `${weekdayLabel}, ${dateLabel}` : dateLabel
    }
    return unknownLabel
  } catch {
    return unknownLabel
  }
}

/**
 * Convert a ScheduleHoliday to ScheduleEventCreate payload
 */
const holidayToEventCreate = (
  holiday: ScheduleHoliday,
  scheduleTimeZone: string,
): ScheduleEventCreate => {
  return {
    dtstart: holiday.dtstart,
    dtend: holiday.dtend,
    duration: holiday.duration,
    summary: holiday.summary,
    fbtype: 'BUSY',
    rrule: holiday.rrule,
    rdate: holiday.rdate,
    exdate: holiday.exdate,
    switchboard_routing: holiday.switchboard_routing || 'HOLIDAY',
    time_zone: scheduleTimeZone,
  }
}

export const AddHolidaysModal: React.FC<AddHolidaysModalProps> = ({
  isOpen,
  onClose,
  scheduleTimeZone,
  holidays,
  isLoadingHolidays = false,
  holidaysError = null,
  onAddHolidays,
  onOpenCustomOverride,
  onNotify,
}) => {
  const { text, locale } = useScheduleI18n()
  const toaster = useMemo(
    () => createScheduleModalNotifier(onNotify),
    [onNotify],
  )

  const [selectedHolidays, setSelectedHolidays] = useState<Set<string>>(
    new Set(),
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const unknownLabel = text('schedule.holidays.common.unknown', 'Unknown')
  const weekdayLongLabels = useMemo<Record<number, string>>(
    () => ({
      1: text('schedule.common.weekdayLong.mon', 'Monday'),
      2: text('schedule.common.weekdayLong.tue', 'Tuesday'),
      3: text('schedule.common.weekdayLong.wed', 'Wednesday'),
      4: text('schedule.common.weekdayLong.thu', 'Thursday'),
      5: text('schedule.common.weekdayLong.fri', 'Friday'),
      6: text('schedule.common.weekdayLong.sat', 'Saturday'),
      7: text('schedule.common.weekdayLong.sun', 'Sunday'),
    }),
    [text],
  )

  const tableHeaderTextStyles = {
    fontFamily: dsText.Body1_Bold.fontFamily,
    fontSize: fontSize(dsText.Body1_Bold.fontSize),
    fontWeight: dsText.Body1_Bold.fontWeight,
    lineHeight: dsText.Body1_Bold.lineHeight,
    color: tokenVar('fg.default'),
  }

  const rowPrimaryTextStyles = {
    fontFamily: dsText.Body1.fontFamily,
    fontSize: fontSize(dsText.Body1.fontSize),
    fontWeight: dsText.Body1.fontWeight,
    lineHeight: dsText.Body1.lineHeight,
    color: tokenVar('fg.default'),
  }

  const rowSecondaryTextStyles = {
    fontFamily: dsText.Body1.fontFamily,
    fontSize: fontSize(dsText.Body1.fontSize),
    fontWeight: dsText.Body1.fontWeight,
    lineHeight: dsText.Body1.lineHeight,
    color: tokenVar('fg.muted'),
  }

  // Reset selection when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedHolidays(new Set())
      setIsSubmitting(false)
    }
  }, [isOpen])

  const handleToggleHoliday = useCallback((summary: string) => {
    setSelectedHolidays((prev) => {
      const next = new Set(prev)
      if (next.has(summary)) {
        next.delete(summary)
      } else {
        next.add(summary)
      }
      return next
    })
  }, [])

  const handleClearSelection = useCallback(() => {
    setSelectedHolidays(new Set())
  }, [])

  const handleAddHolidays = useCallback(async () => {
    if (selectedHolidays.size === 0) {
      toaster.create({
        title: text(
          'schedule.holidays.validation.noneSelectedTitle',
          'No holidays selected',
        ),
        description: text(
          'schedule.holidays.validation.noneSelectedDescription',
          'Please select at least one holiday to add',
        ),
        type: 'warning',
        duration: 3000,
        closable: true,
      })
      return
    }

    setIsSubmitting(true)

    // Convert selected holidays to event create payloads
    const eventsToCreate: ScheduleEventCreate[] = holidays
      .filter((h) => selectedHolidays.has(h.summary))
      .map((h) => holidayToEventCreate(h, scheduleTimeZone))

    onAddHolidays(eventsToCreate)
    onClose()
  }, [
    selectedHolidays,
    holidays,
    scheduleTimeZone,
    onAddHolidays,
    onClose,
    text,
    toaster,
  ])

  const handleOpenCustomOverride = useCallback(() => {
    onClose()
    onOpenCustomOverride?.()
  }, [onClose, onOpenCustomOverride])

  return (
    <Modal
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      title={text('schedule.holidays.modal.title', 'Add Preset Holidays')}
      maxWidth="600px"
      footer={
        <HStack gap={3} w="full" justify="flex-end">
          <Button
            variant="ghost"
            onClick={handleClearSelection}
            disabled={isSubmitting || selectedHolidays.size === 0}
            h="44px"
            borderRadius="4px"
            color={tokenVar('fg.muted')}
            fontFamily={dsText.Body1.fontFamily}
            fontSize={fontSize(dsText.Body1.fontSize)}
            fontWeight={dsText.Body1.fontWeight}
            lineHeight={dsText.Body1.lineHeight}
          >
            {text('schedule.common.clear', 'Clear')}
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            h="44px"
            borderColor={tokenVar('border.default')}
            bg={tokenVar('bg.default')}
            color={tokenVar('fg.default')}
            borderRadius="4px"
            fontFamily={dsText.Body1_Bold.fontFamily}
            fontSize={fontSize(dsText.Body1_Bold.fontSize)}
            fontWeight={dsText.Body1_Bold.fontWeight}
            lineHeight={dsText.Body1_Bold.lineHeight}
            _hover={{ bg: tokenVar('gray.subtle') }}
          >
            {text('schedule.common.cancel', 'Cancel')}
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleAddHolidays}
            disabled={
              isSubmitting || isLoadingHolidays || selectedHolidays.size === 0
            }
            h="44px"
            borderRadius="4px"
            fontFamily={dsText.Body1_Bold.fontFamily}
            fontSize={fontSize(dsText.Body1_Bold.fontSize)}
            fontWeight={dsText.Body1_Bold.fontWeight}
            lineHeight={dsText.Body1_Bold.lineHeight}
          >
            {text(
              'schedule.holidays.actions.addWithCount',
              'Add Holidays ({{count}})',
              { count: selectedHolidays.size },
            )}
          </Button>
        </HStack>
      }
    >
      <Text style={scheduleModalBodyTextStyles} mb={4}>
        {text(
          'schedule.holidays.modal.description',
          'Select the holiday presets you want to add to your override list. You can adjust the hours for each holiday by editing the event after it is created.',
        )}
      </Text>

      {/* Header row */}
      <HStack
        justify="space-between"
        bg={tokenVar('bg.muted')}
        px={3}
        py={2}
        borderRadius="8px"
        mb={2}
      >
        <Text style={tableHeaderTextStyles}>
          {text('schedule.holidays.table.holiday', 'Holiday')}
        </Text>
        <Text style={tableHeaderTextStyles}>
          {text('schedule.holidays.table.nextOccurrence', 'Next Occurrence')}
        </Text>
      </HStack>

      {/* Holiday list */}
      <Box
        maxH="400px"
        overflowY="auto"
        borderWidth="1px"
        borderColor={tokenVar('border.default')}
        borderRadius="8px"
        opacity={isSubmitting ? 0.5 : 1}
        transition="opacity 0.2s"
      >
        {isLoadingHolidays ? (
          <Box textAlign="center" py={8}>
            <Spinner size="lg" />
            <Text mt={2} style={scheduleModalBodyTextStyles}>
              {text('schedule.holidays.loading', 'Loading holidays...')}
            </Text>
          </Box>
        ) : holidaysError ? (
          <Box textAlign="center" py={8}>
            <Text color={tokenVar('fg.error')}>
              {text(
                'schedule.holidays.error.failedToLoad',
                'Failed to load holidays',
              )}
            </Text>
            <Text style={rowSecondaryTextStyles}>{holidaysError.message}</Text>
          </Box>
        ) : holidays.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Text style={scheduleModalBodyTextStyles}>
              {text('schedule.holidays.empty', 'No preset holidays available')}
            </Text>
          </Box>
        ) : (
          <VStack align="stretch" gap={0} divideY="1px">
            {holidays.map((holiday) => {
              const isSelected = selectedHolidays.has(holiday.summary)
              return (
                <HStack
                  key={holiday.summary}
                  justify="space-between"
                  px={3}
                  py={3}
                  cursor="pointer"
                  _hover={{ bg: tokenVar('bg.subtle') }}
                  onClick={() => handleToggleHoliday(holiday.summary)}
                >
                  <HStack gap={3}>
                    <Box onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(e) => {
                          if (e.checked !== isSelected) {
                            handleToggleHoliday(holiday.summary)
                          }
                        }}
                        display="inline-flex"
                        alignItems="center"
                        gap={0}
                      />
                    </Box>
                    <Text style={rowPrimaryTextStyles}>{holiday.summary}</Text>
                  </HStack>
                  <Text style={rowSecondaryTextStyles}>
                    {formatNextOccurrence(
                      holiday,
                      scheduleTimeZone,
                      locale,
                      unknownLabel,
                      weekdayLongLabels,
                    )}
                  </Text>
                </HStack>
              )
            })}
          </VStack>
        )}
      </Box>

      {/* Custom override link */}
      {onOpenCustomOverride && (
        <Box mt={4} p={3} bg={tokenVar('bg.muted')} borderRadius="8px">
          <Text style={scheduleModalBodyTextStyles}>
            {text(
              'schedule.holidays.customPrompt.prefix',
              "Don't see the holiday you're looking for?",
            )}{' '}
            <Link
              color={tokenVar('blue.fg')}
              cursor="pointer"
              onClick={handleOpenCustomOverride}
              fontFamily={dsText.Body1_Bold.fontFamily}
              fontSize={fontSize(dsText.Body1_Bold.fontSize)}
              fontWeight={dsText.Body1_Bold.fontWeight}
              lineHeight={dsText.Body1_Bold.lineHeight}
            >
              {text(
                'schedule.holidays.customPrompt.link',
                'Set up your own special hours',
              )}
            </Link>
            .
          </Text>
        </Box>
      )}

      {/* Submitting indicator */}
      {isSubmitting && (
        <HStack justify="center" mt={4}>
          <Spinner size="sm" />
          <Text style={scheduleModalBodyTextStyles}>
            {text(
              'schedule.holidays.submitting',
              'Updating your schedule, this will just take a second.',
            )}
          </Text>
        </HStack>
      )}
    </Modal>
  )
}
